// ICE-серверы для звонков: STUN + (если настроен на сервере) TURN с краткоживущими кредами.
// Кэш привязан к сессии (sub:sid из access-токена): при выходе/смене аккаунта TURN прежнего
// пользователя не отдаётся, запоздавшие ответы чужой сессии отбрасываются. Пока кэша нет или
// запрос не удался — откат на публичный STUN.
//
// Перед созданием offer/answer сессии вызывают `applyIceServers(pc)`: она ждёт загрузку не дольше
// ICE_WAIT_MS и обновляет конфигурацию соединения, чтобы первый звонок после загрузки страницы
// не уходил на один STUN.
import { api, getAccessToken, isAuthenticated } from './api/client';
import { tokenSessionIdentity } from './api/refreshRace';
import type { RtcIceServersDto } from './api/types';

export const FALLBACK_STUN: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

/** Сколько максимум ждём загрузку ICE перед созданием offer/answer, затем откат на STUN. */
export const ICE_WAIT_MS = 1500;
/** Запас на округление серверного времени до секунд, задержку ответа и рассинхрон часов. */
const SAFETY_MS = 5_000;
const MAX_REFRESH_MARGIN_MS = 5 * 60_000;
/** Не чаще одной неудачной попытки в минуту (401/429/сеть). */
const RETRY_AFTER_FAIL_MS = 60_000;

interface Cache {
  session: string;
  servers: RTCIceServer[];
  /** С этого момента пора обновлять (кэш ещё годен). */
  refreshAt: number;
  /** После этого момента TURN-креды больше не отдаём. */
  hardExpiry: number;
}

let cache: Cache | null = null;
let inflight: { session: string; promise: Promise<void> } | null = null;
let failure: { session: string; at: number } | null = null;

function currentSession(): string | null {
  if (!isAuthenticated()) return null;
  return tokenSessionIdentity(getAccessToken());
}

export function toRtcIceServers(dto: RtcIceServersDto): RTCIceServer[] {
  return dto.iceServers
    .filter((s) => s.urls.length > 0)
    .map((s) => (s.username && s.credential
      ? { urls: s.urls, username: s.username, credential: s.credential }
      : { urls: s.urls }));
}

/** Тест-хук: сбросить кэш. */
export function resetIceServersCache() {
  cache = null;
  inflight = null;
  failure = null;
}

/** Окно обновления: min(5 мин, TTL/5), чтобы короткий TTL не отключал кэш. */
export function refreshMarginMs(ttlMs: number): number {
  return Math.min(MAX_REFRESH_MARGIN_MS, ttlMs / 5);
}

/** Загрузить/обновить кэш; ошибки глотает (откат на STUN). Повторные вызовы делят один запрос. */
export function prefetchIceServers(now: number = Date.now()): Promise<void> {
  const session = currentSession();
  if (!session) return Promise.resolve();
  if (cache && cache.session === session && now < cache.refreshAt) return Promise.resolve();
  if (failure && failure.session === session && now - failure.at < RETRY_AFTER_FAIL_MS) return Promise.resolve();
  if (inflight && inflight.session === session) return inflight.promise;
  const startedAt = now;
  const promise = api<RtcIceServersDto>('/api/v1/rtc/ice-servers')
    .then((dto) => {
      // Пока шёл запрос, пользователь мог выйти или сменить аккаунт — чужой ответ не кладём.
      if (currentSession() !== session) return;
      const servers = toRtcIceServers(dto);
      if (servers.length === 0) {
        cache = null;
        return;
      }
      const ttlMs = dto.ttlSeconds * 1000;
      // Срок считаем от НАЧАЛА запроса: сервер отсчитывает TTL раньше, чем клиент получит ответ.
      const hardExpiry = startedAt + ttlMs - Math.min(SAFETY_MS, ttlMs / 10);
      cache = { session, servers, hardExpiry, refreshAt: Math.min(hardExpiry, startedAt + ttlMs - refreshMarginMs(ttlMs)) };
      failure = null;
    })
    .catch(() => {
      if (currentSession() === session) failure = { session, at: Date.now() };
    })
    .finally(() => {
      if (inflight && inflight.promise === promise) inflight = null;
    });
  inflight = { session, promise };
  return promise;
}

/** Синхронно: годный кэш текущей сессии или STUN-откат; заодно запускает фоновое обновление. */
export function currentIceServers(now: number = Date.now()): RTCIceServer[] {
  void prefetchIceServers(now);
  const session = currentSession();
  if (session && cache && cache.session === session && now < cache.hardExpiry) return cache.servers;
  return FALLBACK_STUN;
}

/** Дождаться загрузки (не дольше timeoutMs) и вернуть актуальный список. */
export async function awaitIceServers(timeoutMs: number = ICE_WAIT_MS): Promise<RTCIceServer[]> {
  const load = prefetchIceServers();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => { timer = setTimeout(resolve, timeoutMs); });
  try {
    await Promise.race([load, timeout]);
  } finally {
    if (timer != null) clearTimeout(timer);
  }
  return currentIceServers();
}

/** Подставить свежие ICE-серверы в уже созданное соединение до начала сбора кандидатов. */
export async function applyIceServers(pc: RTCPeerConnection): Promise<void> {
  const servers = await awaitIceServers();
  try {
    pc.setConfiguration({ ...pc.getConfiguration(), iceServers: servers });
  } catch {
    /* соединение закрыто — менять нечего */
  }
}
